import { Inbox } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

export const inputCls =
  "w-full rounded-lg bg-[#0f1216] border border-white/10 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-[#c6f432]/60 focus:ring-2 focus:ring-[#c6f432]/15 transition-colors";

export const Field = ({ label, error, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-400">{label}</span>
    {children}
    {error && <span className="mt-1 block text-xs text-rose-400" data-testid="field-error">{error}</span>}
  </label>
);

export const Select = ({ options, placeholder, ...p }) => (
  <select className={inputCls} {...p}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => (
      <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
    ))}
  </select>
);

export const Btn = ({ variant = "primary", className = "", ...p }) => {
  const v = {
    primary: "bg-[#c6f432] text-[#0b0d10] hover:bg-[#d6ff55]",
    ghost: "bg-white/5 text-zinc-200 hover:bg-white/10 border border-white/10",
    danger: "bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 border border-rose-500/20",
  }[variant];
  return (
    <button
      className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition-[background-color,transform] active:scale-[0.97] disabled:opacity-50 ${v} ${className}`}
      {...p}
    />
  );
};

export const IconBtn = ({ className = "", ...p }) => (
  <button className={`rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-white/10 hover:text-zinc-100 ${className}`} {...p} />
);

export const Modal = ({ open, onClose, title, children, testid }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#13161b] text-zinc-100 sm:max-w-xl" data-testid={testid}>
      <DialogHeader>
        <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
        <DialogDescription className="text-zinc-500">Fields marked * are required.</DialogDescription>
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export const ConfirmDelete = ({ open, onCancel, onConfirm, what }) => (
  <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent className="border-white/10 bg-[#13161b] text-zinc-100" data-testid="confirm-delete-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {what}?</AlertDialogTitle>
        <AlertDialogDescription className="text-zinc-400">This cannot be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10" data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction className="bg-rose-500 text-white hover:bg-rose-600" onClick={onConfirm} data-testid="confirm-delete-ok">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const Empty = ({ title, hint, testid }) => (
  <div className="flex flex-col items-center justify-center px-6 py-14 text-center" data-testid={testid}>
    <div className="mb-3 rounded-full bg-white/5 p-3"><Inbox className="h-6 w-6 text-zinc-500" /></div>
    <p className="font-semibold text-zinc-200">{title}</p>
    {hint && <p className="mt-1 max-w-sm text-sm text-zinc-500">{hint}</p>}
  </div>
);

export const Card = ({ className = "", ...p }) => (
  <div className={`rounded-2xl border border-white/[0.07] bg-[#111418] ${className}`} {...p} />
);

export const PageHead = ({ title, sub, children }) => (
  <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between animate-rise">
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">{title}</h1>
      {sub && <p className="mt-1 text-sm text-zinc-500">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Pill = ({ tone = "zinc", children, testid }) => {
  const t = {
    green: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
    yellow: "bg-amber-400/10 text-amber-200 border-amber-400/20",
    red: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    blue: "bg-sky-500/10 text-sky-300 border-sky-500/20",
    zinc: "bg-white/5 text-zinc-300 border-white/10",
  }[tone];
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${t}`} data-testid={testid}>{children}</span>;
};

export const statusTone = (s) =>
  ({ Done: "green", Solved: "green", "In Progress": "blue", Pending: "yellow", Open: "yellow" }[s] || "zinc");

export const Table = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-max text-sm">
      <thead>
        <tr className="border-b border-white/[0.07] text-left text-[11px] uppercase tracking-[0.1em] text-zinc-500">
          {head.map((h) => <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold">{h}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/[0.05] text-zinc-300">{children}</tbody>
    </table>
  </div>
);

export const Td = ({ className = "", ...p }) => <td className={`px-4 py-3 align-top ${className}`} {...p} />;
